import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { agentService, utilService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import { Pagination, Score, Panel } from '../../../atoms'
import * as interfaces from '../../../../static/interfaces'
import SvgIcon from 'components/atoms/SvgIcon'
import { LightgalleryItem, LightgalleryProvider, useLightgallery } from 'react-lightgallery'
import SwiperCore, { Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

import AvatarImage from 'components/atoms/AvatarImage'
import SidebarLayout from '../../_layouts/sidebarLayout'

declare var window: interfaces.IWindow

SwiperCore.use([Navigation])

interface IProps {
  user: any
}

interface IReviewImageGalleryItem {
  src: string
  previewSrc: string
}

const AvatarContent: React.VFC<{ review: interfaces.IReview }> = ({ review }) => {
  const reviewerName =
    review.reviewer?.username ||
    review.direct_reservation?.guest_name ||
    review.anonymous_review_author

  return (
    <Avatar>
      {review.reviewer ? (
        <span className="Avatar_Image">
          <AvatarImage avatarURL={review.reviewer.avatar_url} size={40} />
        </span>
      ) : (
        <AvatarImage avatarURL="/images/no-avatar.svg" size={40} />
      )}
      <div className="Avatar_Info">
        {reviewerName && <h4>{reviewerName}</h4>}
        {review.review_date && <span>{review.review_date}</span>}
      </div>
    </Avatar>
  )
}

const ReviewImageSwiper: React.FC<{
  images: IReviewImageGalleryItem[]
  reviewId: number
  isOpen: boolean
}> = ({ images, reviewId, isOpen }) => {
  const { openGallery } = useLightgallery()

  const open = React.useCallback(
    (index: number) => {
      openGallery(`reviewImages-${reviewId}`, index)
    },
    [openGallery, reviewId]
  )

  return (
    <Swiper
      navigation={{
        nextEl: `.button-next-${reviewId}`,
        prevEl: `.button-prev-${reviewId}`,
        disabledClass: 'button-disabled',
      }}
      slidesPerView={'auto'}
      slidesPerGroup={1}
      spaceBetween={8}
      className="reviewSwiper"
    >
      {images.map((image, index) => (
        <SwiperSlide key={`${reviewId}-${index}`}>
          <ImagePreviewButton type="button" isOpen={isOpen} onClick={() => open(index)}>
            <img src={image.previewSrc} alt={`Review image ${index + 1}`} loading="lazy" />
          </ImagePreviewButton>
        </SwiperSlide>
      ))}

      {images.length > 1 && (
        <>
          <button
            type="button"
            className={`button-prev button-prev-${reviewId}`}
            aria-label="Previous image"
          >
            <SvgIcon
              name="keyboard_arrow_left"
              className="icon"
              ariaHidden={true}
              role="presentation"
            />
          </button>
          <button
            type="button"
            className={`button-next button-next-${reviewId}`}
            aria-label="Next image"
          >
            <SvgIcon
              name="keyboard_arrow_right"
              className="icon"
              ariaHidden={true}
              role="presentation"
            />
          </button>
        </>
      )}
    </Swiper>
  )
}

const ReviewImageGallery: React.FC<{
  review: interfaces.IReview
}> = ({ review }) => {
  const images = React.useMemo<IReviewImageGalleryItem[]>(() => {
    if (review.review_image_urls && review.review_image_urls.length > 0) {
      return review.review_image_urls.map(url => ({ src: url, previewSrc: url }))
    }

    if (!review.review_image?.image_url) {
      return []
    }

    return [
      {
        src: review.review_image.large_url || review.review_image.image_url,
        previewSrc: review.review_image.thumb_url || review.review_image.image_url,
      },
    ]
  }, [review.review_image, review.review_image_urls])

  if (images.length === 0) {
    return null
  }

  return (
    <LightgalleryProvider
      plugins={['lg-fullscreen.js', 'lg-zoom.js']}
      lightgallerySettings={{
        zoom: false,
        fullScreen: false,
        download: false,
      }}
    >
      {images.map((image, index) => (
        <LightgalleryItem
          key={`${review.id}-${index}`}
          group={`reviewImages-${review.id}`}
          src={image.src}
        />
      ))}
      <ReviewImageSwiper
        images={images}
        reviewId={review.id}
        isOpen={review.review_image?.is_open !== false}
      />
    </LightgalleryProvider>
  )
}

const Index: React.FC<IProps> = props => {
  const { data: user } = utilService.getDataFromJson(props.user)
  const [reviews, setReviews] = React.useState<interfaces.IReview[]>([])
  const [pagination, setPagination] = React.useState<interfaces.IPager>()

  const fetchReviews = React.useCallback(async (page?: number) => {
    try {
      window.scrollTo({ top: 0 })
      const { reviews: fetchedReviews, pagination: meta } = await agentService.getReviews(page)
      setReviews(fetchedReviews)
      setPagination(meta)
    } catch (e) {
      console.error(e)
    }
  }, [])

  const handleToggleIsOpen = React.useCallback(async (id: number) => {
    try {
      const { review: updated } = await agentService.toggleReviewIsOpen(id)
      setReviews(prev => prev.map(r => (r.id === id ? { ...r, is_open: updated.is_open } : r)))
    } catch (e) {
      console.error(e)
    }
  }, [])

  const handleToggleImageIsOpen = React.useCallback(async (reviewId: number, imageId: number) => {
    try {
      const { reviewImage } = await agentService.toggleReviewImageIsOpen(imageId)
      setReviews(prev =>
        prev.map(r =>
          r.id === reviewId && r.review_image
            ? { ...r, review_image: { ...r.review_image, is_open: reviewImage.is_open } }
            : r
        )
      )
    } catch (e) {
      console.error(e)
    }
  }, [])

  React.useEffect(() => {
    fetchReviews(1)
  }, [fetchReviews])

  return (
    <SidebarLayout user={user}>
      <Panel
        marginType="dashboard"
        title={I18n.t('generic.user_reviews_list', { username: user.username })}
      >
        <Headline>
          <div className="count">
            <Score score={user.average_review} />
            {user.review_count && (
              <span className="review_count">
                ({I18n.t('review.review_count', { count: user.review_count })})
              </span>
            )}
          </div>
        </Headline>
        <Inner className="reviews-list">
          <style type="text/css">{`
            .reviews-list {
              container-type: inline-size;
              container-name: reviews-list;
            }
            @container reviews-list (width < 720px) {
              .review-row {
                flex-direction: column;
              }
              .review-row-head {
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
              }
            }
          `}</style>
          {reviews.map(review => {
            const isOpen = review.is_open !== false
            return (
              <ReviewWrapper className="review-row" key={review.id} isOpen={isOpen}>
                <ReviewHead className="review-row-head">
                  <AvatarContent review={review} />
                  <ToggleButton
                    type="button"
                    isOpen={isOpen}
                    className={isOpen ? 'open' : 'closed'}
                    onClick={() => handleToggleIsOpen(review.id)}
                  >
                    {isOpen ? (
                      <span>
                        <SvgIcon
                          name="visibility_off"
                          className="icon"
                          ariaHidden={true}
                          role="presentation"
                        />
                        {I18n.t('agent.reviews.hide')}
                      </span>
                    ) : (
                      <span>
                        <SvgIcon
                          name="visibility_filled"
                          className="icon"
                          ariaHidden={true}
                          role="presentation"
                        />
                        {I18n.t('agent.reviews.show')}
                      </span>
                    )}
                  </ToggleButton>
                </ReviewHead>
                <ReviewBody isOpen={isOpen}>
                  <div className="Review">
                    <Score score={Math.round(review.rating / 2)} />
                    <div className="TourTitle">
                      <a href={`/posts/${review.reviewable?.slug}`}>
                        {review.reviewable?.name || '-'}
                      </a>
                    </div>
                    <p className="body">{review.body}</p>
                  </div>
                  {review.review_image && (
                    <ReviewImageWrapper isOpen={review.review_image.is_open !== false}>
                      <ReviewImageGallery review={review} />
                      {review.review_image.is_open !== false && (
                        <PublishedTag>
                          <SvgIcon
                            name="visibility_filled"
                            className="icon"
                            ariaHidden={true}
                            role="presentation"
                          />
                          {I18n.t('agent.reviews.image_published')}
                        </PublishedTag>
                      )}
                      <ImageToggleButton
                        className="image-toggle-button"
                        type="button"
                        isOpen={review.review_image.is_open !== false}
                        onClick={() => handleToggleImageIsOpen(review.id, review.review_image!.id)}
                      >
                        {review.review_image.is_open !== false ? (
                          <span>
                            <SvgIcon
                              name="visibility_off"
                              className="icon"
                              ariaHidden={true}
                              role="presentation"
                            />
                            {I18n.t('agent.reviews.image_hide')}
                          </span>
                        ) : (
                          <span>
                            <SvgIcon
                              name="visibility_filled"
                              className="icon"
                              ariaHidden={true}
                              role="presentation"
                            />
                            {I18n.t('agent.reviews.image_show')}
                          </span>
                        )}
                      </ImageToggleButton>
                    </ReviewImageWrapper>
                  )}
                </ReviewBody>
              </ReviewWrapper>
            )
          })}

          {pagination && (
            <Pagination
              onChangePageHandler={page => fetchReviews(page)}
              currentPage={pagination.current_page}
              prevPage={pagination.prev_page}
              nextPage={pagination.next_page}
              totalCount={pagination.total_count}
              totalPages={pagination.total_pages}
            />
          )}
        </Inner>
      </Panel>
    </SidebarLayout>
  )
}

export default Index

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: auto;
  padding: 24px 0;
`

const ReviewWrapper = styled.div<{ isOpen: boolean }>`
  display: flex;
  gap: 16px;
  border: 1px solid
    ${({ isOpen }) => (isOpen ? constants.COLORS.divider : constants.COLORS.divider2)};
  padding: 16px;
  background-color: ${({ isOpen }) =>
    isOpen ? constants.COLORS.white : constants.COLORS.divider2};
  border-radius: 4px;

  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    border: none;
    border-bottom: 1px solid ${constants.COLORS.divider};
  }
`

const ReviewHead = styled.div`
  flex-shrink: 0;
  flex-basis: 24%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  justify-content: space-between;
  > div {
    margin: 0;
    width: auto;
  }
  .Avatar_Info {
    min-width: 0;
    h4 {
      font-size: 14px !important;
      word-break: break-all;
    }
  }
`

const ReviewBody = styled.div<{ isOpen: boolean }>`
  flex-grow: 1;
  display: flex;
  align-items: flex-start;
  gap: 12px;

  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }

  .Review {
    display: flex;
    flex-direction: column;
    flex: 1;

    .TourTitle {
      margin-top: 4px;
      font-weight: bold;
      a {
        color: ${constants.COLORS.primary};
        font-size: 14px;
        font-weight: normal;
        text-decoration: underline;

        &:hover {
          text-decoration: none;
        }
      }
    }
    .body {
      display: block;
      color: ${({ isOpen }) => (isOpen ? constants.COLORS.black : constants.COLORS.subText)};
      margin-top: 4px;
      font-size: 14px;
      width: 100%;
    }
  }
`

const ToggleButton = styled.button<{ isOpen: boolean }>`
  align-self: flex-start;
  line-height: 20px;
  padding: 4px 12px;
  font-size: 12px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid
    ${({ isOpen }) => (isOpen ? constants.COLORS.divider : constants.COLORS.primary)};
  background-color: ${({ isOpen }) => (isOpen ? constants.COLORS.white : constants.COLORS.primary)};
  color: ${({ isOpen }) => (isOpen ? constants.COLORS.black : constants.COLORS.white)};

  &:hover {
    opacity: 0.8;
  }
  span {
    display: flex;
    align-items: center;
    gap: 4px;

    .icon {
      width: 16px;
      height: 16px;
    }
  }
`

const ReviewImageWrapper = styled.div<{ isOpen: boolean }>`
  position: relative;
  flex-shrink: 0;

  .reviewSwiper {
    .swiper-slide {
      width: auto;
      margin-right: 0 !important;
    }
  }

  .button-prev,
  .button-next {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 50%;
    width: 24px;
    height: 24px;
    background-color: rgba(0, 0, 0, 0.5);
    border: 0;
    border-radius: 50%;
    transform: translateY(-50%);
    transition: background-color 0.2s;
    z-index: 2;
    cursor: pointer;

    .icon {
      color: ${constants.COLORS.white};
      width: 16px;
      height: 16px;
    }

    &:hover {
      background-color: rgba(0, 0, 0, 0.75);
    }

    &.button-disabled {
      background-color: rgba(0, 0, 0, 0.25);

      .icon {
        opacity: 0.5;
      }
    }
  }

  .button-prev {
    left: 4px;
  }

  .button-next {
    right: 4px;
  }

  &:hover .image-toggle-button,
  &:focus-within .image-toggle-button {
    opacity: 1;
    pointer-events: auto;
  }

  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    margin-left: 0;
    align-self: flex-start;

    .image-toggle-button {
      opacity: 1;
      pointer-events: auto;
    }

    .button-prev,
    .button-next {
      top: 50%;
    }
  }
`

const ImagePreviewButton = styled.button<{ isOpen: boolean }>`
  display: block;
  padding: 0;
  background: transparent;
  border: 0;
  transition: opacity 0.25s ease-in-out;
  cursor: zoom-in;

  img {
    width: 160px;
    height: 160px;
    object-fit: cover;
    border-radius: 4px;
    border: ${({ isOpen }) => (isOpen ? `3px solid ${constants.COLORS.primary}` : 'none')};
  }

  &:hover {
    opacity: 0.85;
  }

  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    img {
      width: 100%;
      height: auto;
      max-height: 200px;
    }
  }
`

const PublishedTag = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: bold;
  color: ${constants.COLORS.white};
  background-color: ${constants.COLORS.primary};
  border-radius: 4px 0 4px 0;
  pointer-events: none;
  z-index: 2;
  .icon {
    width: 16px;
    height: 16px;
  }
`

const ImageToggleButton = styled.button<{ isOpen: boolean }>`
  position: absolute;
  left: 50%;
  bottom: 12px;
  line-height: 20px;
  transform: translateX(-50%);
  padding: 4px 12px;
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
  border-radius: 16px;
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s ease-in-out;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  background-color: rgba(255, 255, 255, 0.75);
  border: 1px solid ${({ isOpen }) => (isOpen ? constants.COLORS.danger : constants.COLORS.primary)};
  color: ${({ isOpen }) => (isOpen ? constants.COLORS.danger : constants.COLORS.primary)};
  z-index: 2;
  span {
    display: flex;
    align-items: center;
    gap: 4px;

    .icon {
      width: 16px;
      height: 16px;
    }
  }

  &:hover {
    background-color: rgba(255, 255, 255, 1);
  }
`

const Headline = styled.div`
  .count {
    display: flex;
    align-items: center;
    font-size: 12px;
  }
  .Score_StarsWrapper {
    width: 120px;
    height: 24px;
  }
  .Stars_Star {
    width: 24px;
    height: 24px;
  }
  .review_count {
    margin-left: 10px;
  }
`

const Avatar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin: 5px;
  width: 250px;

  .Avatar_Info {
    flex: 1;

    h4 {
      font-size: 16px;
      margin-bottom: 4px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    > span {
      display: block;
      font-size: 0.8em;
      color: gray;
    }
  }
`
